import PerfectScrollbar from 'react-perfect-scrollbar';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme
} from '@material-ui/core';
import {blue, red, yellow} from '@material-ui/core/colors';
import clsx from 'clsx';
import React from 'react';
import {makeStyles} from '@material-ui/styles';
import useRouter from 'use-react-router';
import {TableEditBar} from '../../../components/TableEditBar';
import {PaginatiorInfo} from '../../../graphql/common.datatypes';
import BlockOutlinedIcon from '@material-ui/icons/BlockOutlined';
import NewReleasesOutlinedIcon from '@material-ui/icons/NewReleasesOutlined';
import {GroupMember, GroupMemberRoleTypes, TourRoleTypes} from '../../../graphql/groups.datatypes';
import {formatDate, formatMoney} from '../../../utils/formatters';
import moment from 'moment';
import {getMe} from '../../../graphql/auth';
import {checkIsSuperAdmin} from "../../../utils/checkIsAdmin";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'relative',
    },
    content: {
        padding: 0
    },
    inner: {
      [theme.breakpoints.up('md')]: {
        minWidth: 700,
      },
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center'
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: 'flex-end'
    },
    cancelled: {
        backgroundColor: red[100],
        '&:hover': {
            backgroundColor: red[200]
        }
    },
    cancelledChip: {
        backgroundColor: red[800],
        color: theme.palette.white,
    },
    unapproved: {
        backgroundColor: blue[100],
        '&:hover': {
            backgroundColor: blue[200]
        }
    },
    unapprovedChip: {
        backgroundColor: blue[800],
        color: theme.palette.white,
    },
    pastdue: {
        backgroundColor: yellow[100],
        '&:hover': {
            backgroundColor: yellow[200]
        }
    },
    pastdueChip: {
        backgroundColor: yellow[800]
    },
}));

const Results = (props: {
    className?: string,
    groupsMembers: GroupMember[],
    paginatorInfo?: PaginatiorInfo,
    setPage?: (page: number) => void,
    rowsPerPage?: number,
    setRowsPerPage?: (rowsPerPage: number) => void,
    selectedMember?: GroupMember,
    setSelectedMember?: (groupMember: GroupMember) => void,
    action?: (groupMember: GroupMember) => void,
    setModal?: (modelSelection: string) => void,
    actionTitle?: string,
    hideGroupColumn?: boolean,
    isAdmin?: boolean,
    canEdit?: boolean,
    groupRef?: string,
}) => {
    const {className, rowsPerPage, setPage, setRowsPerPage, paginatorInfo, groupsMembers, selectedMember, setSelectedMember, action, actionTitle, hideGroupColumn, isAdmin, canEdit, groupRef, setModal, ...rest} = props;
    const classes = useStyles();
    const {history} = useRouter();
    const meData = getMe();
    const isSuperAdmin = checkIsSuperAdmin();

    const canClickMember = (groupMember: GroupMember) => {
        return isAdmin || (groupMember.roomMember && meData && meData.data && meData.data.me && meData.data.me.myBillingParties && meData.data.me.myBillingParties.find(item => item.id === groupMember.roomMember!.billing_party_id) !== undefined);
    }

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        if (setPage) setPage(page + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (setRowsPerPage) setRowsPerPage(Number(event.target.value));
    };

    return (
        <div
            {...rest}
            className={clsx(classes.root, className)}
        >
            <Card>
                <CardHeader
                    title={'Group Members'}
                />
                <Divider/>
                <CardContent className={classes.content}>
                    <PerfectScrollbar>
                        <div className={classes.inner}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        {!hideGroupColumn && (<TableCell>Group</TableCell>)}
                                        {isAdmin && (<TableCell>Group Role</TableCell>)}
                                        <TableCell>Tour Role</TableCell>
                                        <TableCell>Billing Party</TableCell>
                                        <TableCell style={{width: 100}}>Check In</TableCell>
                                        <TableCell style={{width: 100}}>Check Out</TableCell>
                                        {isAdmin && (<TableCell style={{width: 100}}>Cost</TableCell>)}
                                        {isAdmin &&(<TableCell style={{width: 100}}>Outstanding</TableCell>)}
                                        {action && (
                                            <TableCell>Actions</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupsMembers.map(groupMember => {
                                        const showPrices = isAdmin || (moment(groupMember.group!.end_date).add(15, 'days').diff(moment(), 'days') > 0);
                                        const payingMember = groupMember.roomMember && groupMember.roomMember.billingParty && groupMember.roomMember.billingParty.payingMembers &&
                                            groupMember.roomMember.billingParty.payingMembers.find(member => member.primary_paying_member);
                                        const unapproved = !groupMember.cancelled && !groupMember.authorised;
                                        const canClick = canClickMember(groupMember);

                                        return (
                                            <TableRow
                                                hover={!groupMember.cancelled}
                                                key={groupMember.id}
                                                className={clsx({
                                                    [classes.cancelled]: groupMember.cancelled,
                                                    [classes.unapproved]: unapproved,
                                                })}
                                                onClick={() => setSelectedMember ? setSelectedMember(groupMember) :
                                                    isAdmin ? history.push('/groupmembers/' + groupMember.id) :
                                                        canClick ? history.push('/mygroups/groupmembers/' + btoa(JSON.stringify({
                                                            id: groupMember.id,
                                                            groupRef,
                                                        }))) : null
                                                }
                                            >
                                                <TableCell>
                                                    {groupMember.first_name} {groupMember.last_name}
                                                    {groupMember.cancelled && (
                                                        <React.Fragment>
                                                            <br/><Chip icon={<BlockOutlinedIcon style={{color: 'white'}}/>} label="Cancelled"
                                                                       className={classes.cancelledChip} size="small"/>
                                                        </React.Fragment>
                                                    )}
                                                    {unapproved && (
                                                        <React.Fragment>
                                                            <br/><Chip icon={<NewReleasesOutlinedIcon />} label="Unapproved"
                                                                       className={classes.unapprovedChip} size="small"/>
                                                        </React.Fragment>
                                                    )}
                                                </TableCell>
                                                {!hideGroupColumn && (<TableCell>{groupMember.group!.name}</TableCell>)}
                                                {isAdmin && (<TableCell>{GroupMemberRoleTypes[groupMember.group_role].name}</TableCell>)}
                                                <TableCell>{TourRoleTypes[groupMember.tour_role].name}</TableCell>
                                                <TableCell>{payingMember && (payingMember.first_name + ' ' + payingMember.last_name)}</TableCell>
                                                <TableCell>{groupMember.roomMember ? formatDate(groupMember.roomMember.start_date) : ''}</TableCell>
                                                <TableCell>{groupMember.roomMember ? formatDate(groupMember.roomMember.end_date) : ''}</TableCell>
                                                {isAdmin && (<TableCell>{showPrices ? ((groupMember.roomMember ? formatMoney(groupMember.roomMember.cost!,true) : 0)) : 'n/a'}</TableCell>)}
                                                {isAdmin && (<TableCell>{showPrices ? ((groupMember.roomMember ? formatMoney(groupMember.roomMember.outstandingBalance!,true) : 0)) : 'n/a'}</TableCell>)}
                                                {action && setModal && (
                                                    <TableCell>
                                                        <Button color="primary" variant="outlined" onClick={(event) => {
                                                            event.stopPropagation();
                                                            action(groupMember);
                                                            setModal('details')
                                                        }}>
                                                            {actionTitle}
                                                        </Button>
                                                        { isSuperAdmin && (
                                                            <Button color="primary" variant="outlined" onClick={(event) => {
                                                            event.stopPropagation();
                                                            action(groupMember);
                                                            setModal('change_room')
                                                        }}>Change Room Party</Button>
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </PerfectScrollbar>
                </CardContent>

                {paginatorInfo && rowsPerPage && (
                    <CardActions className={classes.actions}>
                        <TablePagination
                            component="div"
                            count={paginatorInfo.total}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            page={paginatorInfo.currentPage - 1}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </CardActions>
                )}

            </Card>
            <TableEditBar selected={[]}/>
        </div>
    );
};

export default Results;
